/* eslint-disable react-refresh/only-export-components --
 * Canonical React Context pattern — co-locates the provider
 * component with its consumer hook + the output renderer.
 * Splitting hooks into a separate file produces an orphaned
 * types-only module that obscures the announcer's single-
 * source-of-truth nature; fast-refresh degrades to a full
 * reload when this file changes, which is acceptable for a
 * stable infrastructure module.
 */
/**
 * Screen-reader announcer — a small ``aria-live`` region that
 * components can push messages into for assistive-tech feedback.
 *
 * Mounted at the chrome level (AppShell) so the live region is
 * always present + receives messages from any subtree consumer.
 * The :class:`MultiPaneWorkspace` is the primary producer at
 * Stream C PR 3 — it announces panel add / remove / layout
 * reset / panel maximize + restore per REQ-UI-059. Stream A
 * PR 5 (selection bus) and Stream A PR 6 (display-only mode)
 * will add more producers as they ship.
 *
 * Output strategy:
 *
 * - ``aria-live="polite"`` so announcements don't interrupt
 *   the user's current screen-reader focus — they queue and
 *   speak when the user pauses. ``"assertive"`` would be
 *   appropriate for error / critical states; not used at MVP.
 * - ``role="status"`` reinforces the live-region semantics for
 *   assistive tech that doesn't honor ``aria-live`` directly.
 * - **Visually hidden** via the standard sr-only pattern
 *   (absolute-positioned 1px clip rect) — sighted users don't
 *   see the announcement text; only screen-readers parse it.
 * - **Empty-then-fill cycle:** the announcer clears the
 *   message after a short delay so the next identical message
 *   is announced again (some screen-readers dedupe identical
 *   adjacent strings; clearing forces a re-announce).
 *
 * Cross-references:
 *   - REQ-UI-059 (multi-pane accessibility — screen-reader
 *     announcements for panel events).
 *   - chrome-spec.md 2026-05-26 amendment §"UI preferences menu"
 *     + ADR-024 §8 (accessibility).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * The announcer API exposed via context. Components call
 * :func:`announce` with a one-sentence string. Empty strings
 * are ignored.
 */
export interface AnnouncerApi {
  announce: (message: string) => void;
}

const AnnouncerContext = createContext<AnnouncerApi | null>(null);

/**
 * Wraps subtree with an announcer. Hosts the live-region state
 * + the auto-clear timer that lets identical messages
 * re-announce.
 */
export function AnnouncerProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [message, setMessage] = useState<string>("");
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const announce = useCallback((next: string): void => {
    if (next === "") {
      return;
    }
    // Clear any pending auto-clear so the new message stays
    // visible to the live region long enough to be read.
    if (clearTimerRef.current !== null) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
    // Two-phase update so repeated identical messages re-fire:
    // briefly reset to empty, then set to the new content. The
    // ``Promise.resolve().then`` defers the second update to the
    // next microtask so the empty-then-fill cycle is observable
    // to screen readers.
    setMessage("");
    void Promise.resolve().then(() => {
      setMessage(next);
      clearTimerRef.current = setTimeout(() => {
        setMessage("");
        clearTimerRef.current = null;
      }, 5000);
    });
  }, []);

  // Defensive cleanup — clear the pending timer if the
  // provider unmounts mid-announcement.
  useEffect(() => {
    return () => {
      if (clearTimerRef.current !== null) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
    };
  }, []);

  return (
    <AnnouncerContext.Provider value={{ announce }}>
      {children}
      <AnnouncerOutput message={message} />
    </AnnouncerContext.Provider>
  );
}

/**
 * Internal output renderer — the visually-hidden live region.
 * Not exported; the provider owns the output's position in the
 * tree so consumers don't have to thread it manually.
 */
function AnnouncerOutput({ message }: { message: string }): React.ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="screen-reader-announcer"
      // Visually-hidden via the standard sr-only pattern.
      // Tailwind doesn't have ``sr-only`` by default in our
      // config, so the inline style ensures correctness.
      style={{
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        borderWidth: 0,
      }}
    >
      {message}
    </div>
  );
}

/**
 * Consumer hook for components that want to emit a screen-
 * reader announcement. Returns ``null`` (and the announce
 * call is a no-op) when no provider is mounted — components
 * should treat the announcer as best-effort and not condition
 * any behavior on its presence.
 */
export function useAnnouncer(): AnnouncerApi {
  const ctx = useContext(AnnouncerContext);
  if (ctx === null) {
    // Defensive: emit a console hint in dev mode but don't
    // throw — the announcer is supplementary to the visible UI.
    return {
      announce: () => undefined,
    };
  }
  return ctx;
}
